import {
  AccountCircle,
  Assessment,
  Build,
  BurstMode,
  Business,
  Category,
  CloudUpload,
  Dashboard,
  Event,
  GroupWork,
  Paid,
  PhonelinkSetup,
  PowerSettingsNew,
  QrCode2,
  SupervisedUserCircle,
  Wallpaper,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isUserAuthorized } from "../../helpers";
import { styled } from "@mui/material/styles";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import ContactSupport from "../../assets/contact_support.svg";
import { logout } from "../../reducers/user/userSlice";
import { useSnackbar } from "notistack";

const PREFIX = "DrawerMenu";

const classes = {
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  cursorPointer: `${PREFIX}-cursorPointer`,
  logout: `${PREFIX}-logout`,
  desktopIconSize: `${PREFIX}-desktopIconSize`,
  iconContainer: `${PREFIX}-iconContainer`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    display: "flex",
    minHeight: "56px",
  },

  [`& .${classes.drawer}`]: {
    width: drawerWidth,
    flexShrink: 0,
  },

  [`& .${classes.drawerPaper}`]: {
    width: drawerWidth,
    backgroundColor: "#424242",
  },

  [`& .${classes.cursorPointer}`]: {
    cursor: "pointer",
  },

  [`& .${classes.logout}`]: {
    width: "100%",
    borderTop: "1px solid rgba(255, 255, 255, 0.12)",
  },

  [`& .${classes.desktopIconSize}`]: {
    width: "30px",
    height: "30px",
  },

  [`& .${classes.iconContainer}`]: {
    width: "30px",
    height: "30px",
    borderRadius: "25%",
    padding: "3px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const drawerWidth = 240;

const DrawerMenu = ({ open, setOpen }) => {
  const history = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [myAvatar, setMyAvatar] = useState();
  const loggedIn = useSelector((state) => state.user.loggedIn);
  const userAvatar = useSelector(
    (state) => loggedIn && state.user.currentUser.accountDetails.avatar
  );
  const userRole = useSelector(
    (state) => state.user?.currentUser?.account?.role
  );
  const isSuperAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.isSuperAdmin
  );
  const userRights = useSelector(
    (state) => state.user?.currentUser?.account?.rights
  );
  const accountEmail = useSelector(
    (state) => state.user?.currentUser?.account?.email
  );
  const accountDetailsEmail = useSelector(
    (state) => state.user?.currentUser?.accountDetails?.account?.email
  );

  useEffect(() => {
    const splittedString = userAvatar?.split("/");
    const userId = splittedString
      ? splittedString[splittedString.length - 1].split(".")[0].split("-")[1]
      : 1;
    setMyAvatar(
      userAvatar?.includes("RPM")
        ? `https://api.readyplayer.me/v1/avatars/${userId}.png`
        : userAvatar
    );
  }, [userAvatar]);

  return (
    <Drawer
      className={classes.drawer}
      variant="temporary"
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <Root>
        <Divider />
        <List>
          <ListItem
            button
            key="profile"
            onClick={() => {
              history("/account");
              setOpen(false);
            }}
          >
            <ListItemIcon>
              {myAvatar && (
                <Badge
                  badgeContent="!"
                  color="secondary"
                  overlap="rectangular"
                  invisible={accountEmail || accountDetailsEmail ? true : false}
                  sx={{
                    padding: "0 4px",
                  }}
                >
                  <Avatar
                    src={myAvatar}
                    alt="my Avatar"
                    className={clsx(
                      classes.cursorPointer,
                      classes.desktopIconSize
                    )}
                  />
                </Badge>
              )}
              {!myAvatar && (
                <Badge
                  badgeContent="!"
                  color="secondary"
                  overlap="rectangular"
                  invisible={accountEmail || accountDetailsEmail ? true : false}
                  sx={{
                    padding: "0 4px",
                  }}
                >
                  <AccountCircle className={classes.cursorPointer} />
                </Badge>
              )}
            </ListItemIcon>
            <ListItemText
              primary={t("topMenu.profile")}
              onClick={() => {
                history("/account");
                setOpen(false);
              }}
            />
          </ListItem>
          <Divider />
          <ListItem
            button
            key="upload"
            onClick={() => {
              history("/upload");
              setOpen(false);
            }}
          >
            <ListItemIcon>
              <CloudUpload className={classes.desktopIconSize} />
            </ListItemIcon>
            <ListItemText primary={t("topMenu.upload")} />
          </ListItem>
          <Divider />
          <ListItem
            button
            key="schedule"
            onClick={() => {
              history("/schedule");
              setOpen(false);
            }}
          >
            <ListItemIcon>
              <Event className={classes.desktopIconSize} />
            </ListItemIcon>
            <ListItemText primary={t("topMenu.schedule")} />
          </ListItem>
          <Divider />
          <ListItem
            button
            key="statistic"
            onClick={() => {
              history("/statistics");
              setOpen(false);
            }}
          >
            <ListItemIcon>
              <Assessment className={classes.desktopIconSize} />
            </ListItemIcon>
            <ListItemText primary={t("topMenu.statistics")} />
          </ListItem>
        </List>
        <div>
          <Divider />
          <ListSubheader sx={{ backgroundColor: "rgba(42, 42, 42, 0.7)" }}>
            Administration
          </ListSubheader>
          <List className={classes.admin}>
            {isUserAuthorized(userRole, userRights, "exp") && (
              <ListItem
                button
                key="admin-my-expariences"
                onClick={() => {
                  history("/myExpariences");
                  setOpen(false);
                }}
              >
                <ListItemIcon>
                  <div
                    className={classes.iconContainer}
                    style={{ backgroundColor: "#1c364f" }}
                  >
                    <Dashboard />
                  </div>
                </ListItemIcon>
                <ListItemText primary={t("topMenu.adminExparience")} />
              </ListItem>
            )}
            {isUserAuthorized(userRole, userRights, "designs") && (
              <ListItem
                button
                key="admin-myDesigns"
                onClick={() => {
                  history("/myDesigns");
                  setOpen(false);
                }}
              >
                <ListItemIcon>
                  <div
                    className={classes.iconContainer}
                    style={{ backgroundColor: "#ea58a1" }}
                  >
                    <BurstMode />
                  </div>
                </ListItemIcon>
                <ListItemText primary={t("topMenu.myDesigns")} />
              </ListItem>
            )}
            {(isSuperAdmin ||
              isUserAuthorized(userRole, userRights, "faz")) && (
              <ListItem
                button
                key="admin-dma"
                onClick={() => {
                  history("/admin/dma/page");
                  setOpen(false);
                }}
              >
                <ListItemIcon>
                  <div
                    className={classes.iconContainer}
                    style={{ backgroundColor: "#09a137" }}
                  >
                    <PhonelinkSetup />
                  </div>
                </ListItemIcon>
                <ListItemText primary={t("topMenu.adminHome")} />
              </ListItem>
            )}
            {(isSuperAdmin ||
              isUserAuthorized(userRole, userRights, "rights")) && (
              <ListItem
                button
                key="admin-users-rights"
                onClick={() => {
                  history("/admin/users?mode=rights");
                  setOpen(false);
                }}
              >
                <ListItemIcon>
                  <div
                    className={classes.iconContainer}
                    style={{ backgroundColor: "#09a137" }}
                  >
                    <Build />
                  </div>
                </ListItemIcon>
                <ListItemText primary={t("topMenu.adminRights")} />
              </ListItem>
            )}
            {(isSuperAdmin ||
              isUserAuthorized(userRole, userRights, "classes")) && (
              <ListItem
                button
                key="admin-classes"
                onClick={() => {
                  history("/classes");
                  setOpen(false);
                }}
              >
                <ListItemIcon>
                  <div
                    className={classes.iconContainer}
                    style={{ backgroundColor: "#09a137" }}
                  >
                    <Build />
                  </div>
                </ListItemIcon>
                <ListItemText primary={t("topMenu.classes")} />
              </ListItem>
            )}
            {(isSuperAdmin ||
              isUserAuthorized(userRole, userRights, "payments")) && (
              <ListItem
                button
                key="admin-payments"
                onClick={() => {
                  history("/admin/payments");
                  setOpen(false);
                }}
              >
                <ListItemIcon>
                  <div
                    className={classes.iconContainer}
                    style={{ backgroundColor: "#09a137" }}
                  >
                    <Paid />
                  </div>
                </ListItemIcon>
                <ListItemText primary={t("topMenu.adminPayments")} />
              </ListItem>
            )}
            {(isSuperAdmin ||
              isUserAuthorized(userRole, userRights, "QRCodes")) && (
              <ListItem
                button
                key="admin-QRCodes"
                onClick={() => {
                  history("/admin/QRCodes");
                  setOpen(false);
                }}
              >
                <ListItemIcon>
                  <div
                    className={classes.iconContainer}
                    style={{ backgroundColor: "#09a137" }}
                  >
                    <QrCode2 />
                  </div>
                </ListItemIcon>
                <ListItemText primary={t("topMenu.qrCodes")} />
              </ListItem>
            )}
            {(isSuperAdmin ||
              isUserAuthorized(userRole, userRights, "Hdr")) && (
              <ListItem
                button
                key="admin-Hdr"
                onClick={() => {
                  history("/admin/hdr");
                  setOpen(false);
                }}
              >
                <ListItemIcon>
                  <div
                    className={classes.iconContainer}
                    style={{ backgroundColor: "#09a137" }}
                  >
                    <Wallpaper />
                  </div>
                </ListItemIcon>
                <ListItemText primary={t("topMenu.hdr")} />
              </ListItem>
            )}
            {(isSuperAdmin ||
              isUserAuthorized(userRole, userRights, "models")) && (
              <ListItem
                button
                key="admin-models"
                onClick={() => {
                  history("/admin/models");
                  setOpen(false);
                }}
              >
                <ListItemIcon>
                  <div
                    className={classes.iconContainer}
                    style={{ backgroundColor: "#09a137" }}
                  >
                    <WallpaperIcon />
                  </div>
                </ListItemIcon>
                <ListItemText primary={t("topMenu.models")} />
              </ListItem>
            )}
            {userRole === "ADMIN" && (
              <React.Fragment>
                {isUserAuthorized(userRole, userRights, "grafana") && (
                  <ListItem
                    button
                    key="admin-grafana"
                    onClick={() => {
                      window.open(process.env.REACT_APP_GRAFANA_URL, "_blank");
                      setOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      <div
                        className={classes.iconContainer}
                        style={{ backgroundColor: "#D3AF5F" }}
                      >
                        <Assessment />
                      </div>
                    </ListItemIcon>
                    <ListItemText primary={t("dashboard.grafana")} />
                  </ListItem>
                )}
                {isUserAuthorized(userRole, userRights, "cat") && (
                  <ListItem
                    button
                    key="admin-categories"
                    onClick={() => {
                      history("/admin/categories");
                      setOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      <div
                        className={classes.iconContainer}
                        style={{ backgroundColor: "#af2819" }}
                      >
                        <Category />
                      </div>
                    </ListItemIcon>
                    <ListItemText primary={t("topMenu.adminCategories")} />
                  </ListItem>
                )}
                {isUserAuthorized(userRole, userRights, "camp") && (
                  <ListItem
                    button
                    key="admin-campaigns"
                    onClick={() => {
                      history("/admin/campaigns");
                      setOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      <div
                        className={classes.iconContainer}
                        style={{ backgroundColor: "#458faf" }}
                      >
                        <GroupWork />
                      </div>
                    </ListItemIcon>
                    <ListItemText primary={t("topMenu.adminCampaigns")} />
                  </ListItem>
                )}
                {(isSuperAdmin ||
                  isUserAuthorized(userRole, userRights, "users")) && (
                  <ListItem
                    button
                    key="admin-users"
                    onClick={() => {
                      history("/admin/users");
                      setOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      <div
                        className={classes.iconContainer}
                        style={{ backgroundColor: "#af964a" }}
                      >
                        <SupervisedUserCircle />
                      </div>
                    </ListItemIcon>
                    <ListItemText primary={t("topMenu.adminUsers")} />
                  </ListItem>
                )}
                {isUserAuthorized(userRole, userRights, "ent") && (
                  <ListItem
                    button
                    key="admin-entities"
                    onClick={() => {
                      history("/admin/entities");
                      setOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      <div
                        className={classes.iconContainer}
                        style={{ backgroundColor: "#bee455" }}
                      >
                        <Business />
                      </div>
                    </ListItemIcon>
                    <ListItemText primary={t("topMenu.adminEntities")} />
                  </ListItem>
                )}
                {isUserAuthorized(userRole, userRights, "journeys") && (
                  <ListItem
                    button
                    key="admin-journeys"
                    onClick={() => {
                      history("/admin/journeys");
                      setOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      <div
                        className={classes.iconContainer}
                        style={{ backgroundColor: "#bee455" }}
                      >
                        <Business />
                      </div>
                    </ListItemIcon>
                    <ListItemText primary={t("topMenu.adminJourneys")} />
                  </ListItem>
                )}
              </React.Fragment>
            )}
          </List>
        </div>
      </Root>
      <div>
        <List>
          <a
            style={{ display: "flex", textDecoration: "none" }}
            href={`mailto:hello@artdesignstory.fr?subject=${t(
              "topMenu.mailSubject"
            )}`}
            target="_blank"
            rel="noreferrer"
          >
            <ListItem style={{ marginTop: "1.5rem" }} button key="contact us">
              <img
                id="contactSupport"
                alt="contact-support-icon"
                src={ContactSupport}
                style={{
                  marginRight: "32px",
                }}
              />
              <Typography variant="body1">{t("topMenu.contactUs")}</Typography>
            </ListItem>
          </a>
        </List>
        <Root>
          <div className={classes.logout}>
            <List>
              <ListItem
                button
                key="disconnect"
                onClick={(e) => {
                  setOpen(false);
                  enqueueSnackbar(t("toasts.logout"), {
                    variant: "success",
                  });
                  dispatch(logout());
                  history("/");
                }}
              >
                <ListItemIcon>
                  <PowerSettingsNew />
                </ListItemIcon>
                <ListItemText primary={t("topMenu.logout")} />
              </ListItem>
            </List>
          </div>
        </Root>
      </div>
    </Drawer>
  );
};

export default DrawerMenu;
